import dayjs from 'dayjs'
import { RecurringSchedule } from '../../../domain/recurringSchedule'
import { calcStartWeek } from '../../../services/recurringScheduleService'
import { RepeatingEventConfig } from '../scheduleApi'
import { EventFormData } from './eventFormTypes'
import { expandRecurrenceDates } from '../CreateEvent/recurrenceExpander'

export type CadenceFormData = {
  formValues: EventFormData
  repeatingEventConfig: RepeatingEventConfig | null
}

export type WorkareaScheduleFormValues = {
  repeatUnit: string
  repeatCount: number
  repeatEndType: 'date' | 'occurrences'
  repeatOccurrences: number
  repeatEndsOn: dayjs.Dayjs | null
  startDate: dayjs.Dayjs | null
  repeatPlace: string[]
}

const weekOrdinals = ['1st', '2nd', '3rd', '4th', '5th']

export const repeatPlaceFromDate = (date: dayjs.Dayjs): string[] => {
  const weekNumber = calcStartWeek(date.toISOString())
  return [weekOrdinals[weekNumber - 1] || '1st']
}

export const parseWorkareaScheduleForEvent = (configJson: string): WorkareaScheduleFormValues => {
  const config: RecurringSchedule = JSON.parse(configJson)
  const startDate = config.startDate ? dayjs(config.startDate) : null

  const isOccurrences = config.endType === 'occurrences' && config.occurrences > 0
  const place = startDate ? repeatPlaceFromDate(startDate) : ['1st']

  let repeatEndsOn: dayjs.Dayjs | null = null
  if (isOccurrences && startDate) {
    const searchBound = startDate.add(
      config.occurrences * config.repeatCount,
      config.repeatUnits as dayjs.ManipulateType,
    )
    const dates = expandRecurrenceDates({
      startDate,
      endDate: searchBound,
      repeatUnits: config.repeatUnits,
      repeatCount: config.repeatCount,
      repeatingDayUnit: startDate.format('dddd').toLowerCase(),
      repeatPlace: place.join(','),
      repeatMonth: null,
    })
    if (dates.length >= config.occurrences) {
      repeatEndsOn = dates[config.occurrences - 1]
    } else {
      repeatEndsOn = dates[dates.length - 1] ?? startDate
    }
  } else if (config.endType === 'date' && config.endDate && dayjs(config.endDate).isAfter(dayjs(), 'day')) {
    repeatEndsOn = dayjs(config.endDate)
  }

  return {
    repeatUnit: config.repeatUnits,
    repeatCount: config.repeatCount,
    repeatEndType: isOccurrences ? 'occurrences' : 'date',
    repeatOccurrences: isOccurrences ? config.occurrences : 1,
    startDate,
    repeatEndsOn,
    repeatPlace: place,
  }
}

export const workareaRepeatLabel = (configJson: string): string => {
  const config: RecurringSchedule = JSON.parse(configJson)
  if (config.repeatCount > 1) {
    const pluralUnits: Record<string, string> = { day: 'Days', week: 'Weeks', month: 'Months', year: 'Years' }
    return `Every ${config.repeatCount} ${pluralUnits[config.repeatUnits] || ''}`
  }
  const labels: Record<string, string> = { day: 'Daily', week: 'Weekly', month: 'Monthly', year: 'Annually' }
  return labels[config.repeatUnits] || ''
}

export const isWorkareaRecurringEnabled = (configJson: string | null): boolean => {
  if (!configJson) {
    return false
  }
  try {
    const config: RecurringSchedule = JSON.parse(configJson)
    return config.isRecurring === true
  } catch {
    return false
  }
}

